import { useState, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { getButtonClasses } from "../../../utils/buttonStyles";

/** Balance the Ball: tilt the platform, collect coins, beat your high score. */

const GRAVITY = 18;
const FRICTION = 2.5;
const MAX_TILT_DEG = 15;
const MAX_TILT = (MAX_TILT_DEG * Math.PI) / 180;
const TILT_SPEED = 1.8;
const PLATFORM_WIDTH = 12;
const PLATFORM_DEPTH = 8;
const PLATFORM_HEIGHT = 0.8;
const BALL_RADIUS = 0.5;
const CAMERA_FOLLOW = 0.08;
const BALL_BOUND_PAD = 0.3;
const COIN_RADIUS = 0.35;
const COIN_HEIGHT = 0.12;
const COIN_POINTS = 10;
const COIN_SPAWN_MARGIN = 2.5;
const MILESTONES = [15, 30, 45, 60, 90, 120];
const DANGER_EDGE_DIST = 1.5;
const HIGH_SCORE_KEY = "balanceBallHighScore";

export interface BalanceBallGameProps {
  onBack: () => void;
}

export function BalanceBallGame({ onBack }: BalanceBallGameProps) {
  const isMobile = useIsMobile();

  const [gameState, setGameState] = useState<"idle" | "playing" | "gameOver">("idle");
  const [survivalTime, setSurvivalTime] = useState(0);
  const [finalTime, setFinalTime] = useState(0);
  const [score, setScore] = useState(0);
  const [milestoneToast, setMilestoneToast] = useState<string | null>(null);
  const [dangerZone, setDangerZone] = useState(false);
  const [highScore, setHighScore] = useState(() =>
    parseFloat(localStorage.getItem(HIGH_SCORE_KEY) || "0")
  );
  const [isNewRecord, setIsNewRecord] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const platformGroupRef = useRef<THREE.Group | null>(null);
  const ballMeshRef = useRef<THREE.Mesh | null>(null);
  const ballShadowRef = useRef<THREE.Mesh | null>(null);

  const tiltXRef = useRef(0);
  const tiltZRef = useRef(0);
  const ballPosRef = useRef(new THREE.Vector3(0, BALL_RADIUS, 0));
  const ballVelRef = useRef(new THREE.Vector3(0, 0, 0));
  const keysRef = useRef({ up: false, down: false, left: false, right: false });
  const gyroRef = useRef({ beta: 90, gamma: 0 });
  const useGyroRef = useRef(false);
  const rafIdRef = useRef<number>(0);
  const startTimeRef = useRef(0);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cameraTargetRef = useRef(new THREE.Vector3(0, 4, 10));
  const gameStateRef = useRef<"idle" | "playing" | "gameOver">("idle");
  const lastMilestoneRef = useRef(0);
  const coinsRef = useRef<{ id: number; mesh: THREE.Mesh; x: number; z: number }[]>([]);
  const particlesRef = useRef<{ mesh: THREE.Mesh; phase: number; speed: number }[]>([]);
  const setScoreRef = useRef(setScore);
  const setMilestoneToastRef = useRef(setMilestoneToast);
  const setDangerZoneRef = useRef(setDangerZone);
  const setHighScoreRef = useRef(setHighScore);
  const setNewRecordRef = useRef(setIsNewRecord);
  const highScoreRef = useRef(highScore);
  setScoreRef.current = setScore;
  setMilestoneToastRef.current = setMilestoneToast;
  setDangerZoneRef.current = setDangerZone;
  setHighScoreRef.current = setHighScore;
  setNewRecordRef.current = setIsNewRecord;
  highScoreRef.current = highScore;

  const getPlatformPlane = useCallback((outPoint: THREE.Vector3, outNormal: THREE.Vector3) => {
    const group = platformGroupRef.current;
    if (!group) return;
    group.updateMatrixWorld(true);
    const topCenterLocal = new THREE.Vector3(0, 0, 0);
    outPoint.copy(topCenterLocal).applyMatrix4(group.matrixWorld);
    outNormal.set(0, 1, 0).transformDirection(group.matrixWorld);
  }, []);

  const clampTilt = useCallback(() => {
    tiltXRef.current = Math.max(-MAX_TILT, Math.min(MAX_TILT, tiltXRef.current));
    tiltZRef.current = Math.max(-MAX_TILT, Math.min(MAX_TILT, tiltZRef.current));
  }, []);

  const updateBall = useCallback(
    (dt: number) => {
      const planePoint = new THREE.Vector3();
      const planeNormal = new THREE.Vector3();
      getPlatformPlane(planePoint, planeNormal);

      const gravity = new THREE.Vector3(0, -GRAVITY, 0);
      const tangentAcc = gravity
        .clone()
        .sub(planeNormal.clone().multiplyScalar(gravity.dot(planeNormal)));
      ballVelRef.current.add(tangentAcc.multiplyScalar(dt));
      ballVelRef.current.y = 0;

      ballPosRef.current.add(ballVelRef.current.clone().multiplyScalar(dt));

      const toBall = ballPosRef.current.clone().sub(planePoint);
      const dist = toBall.dot(planeNormal);
      ballPosRef.current.sub(planeNormal.clone().multiplyScalar(dist));
      ballPosRef.current.y += BALL_RADIUS;

      const len = ballVelRef.current.length();
      if (len > 0) {
        const frictionFactor = Math.max(0, 1 - FRICTION * dt);
        ballVelRef.current.multiplyScalar(frictionFactor);
      }

      const ballMesh = ballMeshRef.current;
      const ballShadow = ballShadowRef.current;
      if (ballMesh) ballMesh.position.copy(ballPosRef.current);
      if (ballShadow) ballShadow.position.set(ballPosRef.current.x, planePoint.y + 0.01, ballPosRef.current.z);
    },
    [getPlatformPlane]
  );

  const updatePlatform = useCallback(
    (dt: number) => {
      const keys = keysRef.current;
      if (keys.up) tiltXRef.current += TILT_SPEED * dt;
      if (keys.down) tiltXRef.current -= TILT_SPEED * dt;
      if (keys.left) tiltZRef.current += TILT_SPEED * dt;
      if (keys.right) tiltZRef.current -= TILT_SPEED * dt;
      if (useGyroRef.current) {
        const k = 0.015;
        tiltXRef.current = THREE.MathUtils.clamp((gyroRef.current.beta - 90) * k, -MAX_TILT, MAX_TILT);
        tiltZRef.current = THREE.MathUtils.clamp(-gyroRef.current.gamma * k, -MAX_TILT, MAX_TILT);
      }
      clampTilt();
      const group = platformGroupRef.current;
      if (group) {
        group.rotation.x = tiltXRef.current;
        group.rotation.z = tiltZRef.current;
      }
    },
    [clampTilt]
  );

  const isBallOffPlatform = useCallback(() => {
    const halfW = PLATFORM_WIDTH / 2 - BALL_BOUND_PAD;
    const halfD = PLATFORM_DEPTH / 2 - BALL_BOUND_PAD;
    const p = ballPosRef.current;
    return Math.abs(p.x) > halfW || Math.abs(p.z) > halfD;
  }, []);

  const handleStart = useCallback(() => {
    tiltXRef.current = 0;
    tiltZRef.current = 0;
    ballPosRef.current.set(0, BALL_RADIUS, 0);
    ballVelRef.current.set(0, 0, 0);
    const group = platformGroupRef.current;
    if (group) {
      group.rotation.x = 0;
      group.rotation.z = 0;
    }
    startTimeRef.current = performance.now();
    lastMilestoneRef.current = 0;
    gameStateRef.current = "playing";
    setGameState("playing");
    setSurvivalTime(0);
    setFinalTime(0);
    setScore(0);
    setMilestoneToast(null);
    setDangerZone(false);
    setIsNewRecord(false);
    containerRef.current?.focus();

    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      const t = (performance.now() - startTimeRef.current) / 1000;
      setSurvivalTime(() => t);
      const halfW = PLATFORM_WIDTH / 2 - BALL_BOUND_PAD;
      const halfD = PLATFORM_DEPTH / 2 - BALL_BOUND_PAD;
      const p = ballPosRef.current;
      const distToEdge = Math.min(halfW - Math.abs(p.x), halfD - Math.abs(p.z));
      setDangerZoneRef.current(distToEdge < DANGER_EDGE_DIST);
    }, 100);
  }, []);

  const handleRestart = useCallback(() => {
    handleStart();
  }, [handleStart]);

  const requestGyro = useCallback(() => {
    if (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof (DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<string> }).requestPermission === "function"
    ) {
      (DeviceOrientationEvent as unknown as { requestPermission: () => Promise<string> })
        .requestPermission()
        .then((perm: string) => {
          if (perm === "granted") {
            useGyroRef.current = true;
          }
        })
        .catch(() => {});
    } else if (typeof DeviceOrientationEvent !== "undefined") {
      useGyroRef.current = true;
    }
  }, []);

  // Three.js init and game loop
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0c1222);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.set(0, 8, 12);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const ambient = new THREE.AmbientLight(0x506080, 0.5);
    scene.add(ambient);
    const hemi = new THREE.HemisphereLight(0x87ceeb, 0x2a3f5f, 0.5);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 0.85);
    dir.position.set(8, 16, 8);
    dir.castShadow = true;
    dir.shadow.mapSize.width = 1024;
    dir.shadow.mapSize.height = 1024;
    dir.shadow.camera.near = 0.5;
    dir.shadow.camera.far = 50;
    dir.shadow.camera.left = -15;
    dir.shadow.camera.right = 15;
    dir.shadow.camera.top = 15;
    dir.shadow.camera.bottom = -15;
    dir.shadow.bias = -0.0001;
    scene.add(dir);
    const rim = new THREE.DirectionalLight(0x06b6d4, 0.25);
    rim.position.set(-5, 5, -5);
    scene.add(rim);

    const platformGroup = new THREE.Group();
    scene.add(platformGroup);
    platformGroupRef.current = platformGroup;

    const platformGeo = new THREE.BoxGeometry(PLATFORM_WIDTH, PLATFORM_HEIGHT, PLATFORM_DEPTH);
    const platformMat = new THREE.MeshStandardMaterial({
      color: 0x1e3a5f,
      roughness: 0.6,
      metalness: 0.15,
    });
    const platform = new THREE.Mesh(platformGeo, platformMat);
    platform.castShadow = true;
    platform.receiveShadow = true;
    platform.position.y = -PLATFORM_HEIGHT / 2;
    platformGroup.add(platform);
    const edgeGeo = new THREE.BoxGeometry(PLATFORM_WIDTH + 0.15, PLATFORM_HEIGHT + 0.05, PLATFORM_DEPTH + 0.15);
    const edgeMat = new THREE.MeshStandardMaterial({
      color: 0x0ea5e9,
      roughness: 0.5,
      metalness: 0.3,
      transparent: true,
      opacity: 0.4,
    });
    const edge = new THREE.Mesh(edgeGeo, edgeMat);
    edge.position.y = -PLATFORM_HEIGHT / 2 - 0.025;
    platformGroup.add(edge);

    const ballGeo = new THREE.SphereGeometry(BALL_RADIUS, 32, 32);
    const ballMat = new THREE.MeshStandardMaterial({
      color: 0x06b6d4,
      roughness: 0.3,
      metalness: 0.2,
      emissive: 0x0891b2,
      emissiveIntensity: 0.12,
    });
    const ballMesh = new THREE.Mesh(ballGeo, ballMat);
    ballMesh.castShadow = true;
    ballMesh.receiveShadow = true;
    scene.add(ballMesh);
    ballMeshRef.current = ballMesh;

    const shadowGeo = new THREE.CircleGeometry(BALL_RADIUS * 1.2, 24);
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.35,
    });
    const ballShadow = new THREE.Mesh(shadowGeo, shadowMat);
    ballShadow.rotation.x = -Math.PI / 2;
    scene.add(ballShadow);
    ballShadowRef.current = ballShadow;

    const safeHalfW = PLATFORM_WIDTH / 2 - COIN_SPAWN_MARGIN;
    const safeHalfD = PLATFORM_DEPTH / 2 - COIN_SPAWN_MARGIN;
    let coinId = 0;
    const coinWorldPos = new THREE.Vector3();
    const createCoin = () => {
      const x = (Math.random() * 2 - 1) * safeHalfW;
      const z = (Math.random() * 2 - 1) * safeHalfD;
      const geo = new THREE.CylinderGeometry(COIN_RADIUS, COIN_RADIUS, COIN_HEIGHT, 24);
      const mat = new THREE.MeshStandardMaterial({
        color: 0xfbbf24,
        metalness: 0.8,
        roughness: 0.2,
        emissive: 0x92400e,
        emissiveIntensity: 0.15,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, COIN_HEIGHT / 2, z);
      mesh.castShadow = true;
      platformGroup.add(mesh);
      coinsRef.current.push({ id: ++coinId, mesh, x, z });
    };
    for (let i = 0; i < 3; i++) createCoin();

    for (let i = 0; i < 8; i++) {
      const geo = new THREE.SphereGeometry(0.08, 8, 8);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x67e8f9,
        transparent: true,
        opacity: 0.4,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(
        (Math.random() * 2 - 1) * 14,
        2 + Math.random() * 6,
        (Math.random() * 2 - 1) * 10
      );
      scene.add(mesh);
      particlesRef.current.push({
        mesh,
        phase: Math.random() * Math.PI * 2,
        speed: 0.3 + Math.random() * 0.4,
      });
    }

    const onResize = () => {
      if (!container || !camera || !renderer) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);
    onResize();

    let lastTime = performance.now();
    const loop = () => {
      rafIdRef.current = requestAnimationFrame(loop);
      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const isPlaying = gameStateRef.current === "playing";
      if (!isPlaying) {
        particlesRef.current.forEach((p) => {
          p.mesh.position.y += Math.sin(now * 0.001 * p.speed + p.phase) * 0.008;
          if (p.mesh.position.y > 10) p.mesh.position.y = 0;
          if (p.mesh.position.y < 0) p.mesh.position.y = 10;
        });
        renderer.render(scene, camera);
        return;
      }

      updatePlatform(dt);
      updateBall(dt);

      const ballPos = ballPosRef.current;
      const t = (now - startTimeRef.current) / 1000;

      for (let i = coinsRef.current.length - 1; i >= 0; i--) {
        const coin = coinsRef.current[i];
        coin.mesh.rotation.y += dt * 2.5;
        coin.mesh.getWorldPosition(coinWorldPos);
        const dist = ballPos.distanceTo(coinWorldPos);
        if (dist < BALL_RADIUS + COIN_RADIUS) {
          platformGroup.remove(coin.mesh);
          coin.mesh.geometry.dispose();
          (coin.mesh.material as THREE.Material).dispose();
          coinsRef.current.splice(i, 1);
          setScoreRef.current((s: number) => s + COIN_POINTS);
          createCoin();
        }
      }

      particlesRef.current.forEach((p) => {
        p.mesh.position.y += Math.sin(now * 0.001 * p.speed + p.phase) * 0.008;
        if (p.mesh.position.y > 10) p.mesh.position.y = 0;
        if (p.mesh.position.y < 0) p.mesh.position.y = 10;
      });

      for (const m of MILESTONES) {
        if (t >= m && lastMilestoneRef.current < m) {
          lastMilestoneRef.current = m;
          setMilestoneToastRef.current(m + " seconds! 🎉");
          setTimeout(() => setMilestoneToastRef.current(null), 2000);
          break;
        }
      }

      const target = cameraTargetRef.current;
      target.set(
        ballPos.x * 0.3,
        4 + ballPos.y * 0.2,
        10 + ballPos.z * 0.2
      );
      camera.position.lerp(target, CAMERA_FOLLOW);
      const lookAt = new THREE.Vector3(ballPos.x * 0.5, 0, ballPos.z * 0.5);
      camera.lookAt(lookAt);

      if (isBallOffPlatform()) {
        gameStateRef.current = "gameOver";
        setFinalTime(t);
        setGameState("gameOver");
        if (t > highScoreRef.current) {
          highScoreRef.current = t;
          setHighScoreRef.current(t);
          setNewRecordRef.current(true);
          try {
            localStorage.setItem(HIGH_SCORE_KEY, String(t));
          } catch (_) {}
        }
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }

      renderer.render(scene, camera);
    };

    gameStateRef.current = gameState;
    rafIdRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener("resize", onResize);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      coinsRef.current.forEach((c) => {
        c.mesh.geometry.dispose();
        (c.mesh.material as THREE.Material).dispose();
      });
      coinsRef.current = [];
      particlesRef.current.forEach((p) => {
        p.mesh.geometry.dispose();
        (p.mesh.material as THREE.Material).dispose();
      });
      particlesRef.current = [];
      if (container && renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      platformGeo.dispose();
      platformMat.dispose();
      edgeGeo.dispose();
      edgeMat.dispose();
      ballGeo.dispose();
      ballMat.dispose();
      shadowGeo.dispose();
      shadowMat.dispose();
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      platformGroupRef.current = null;
      ballMeshRef.current = null;
      ballShadowRef.current = null;
    };
  }, []);

  // Keep game state ref in sync for loop
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent, down: boolean) => {
      switch (e.code) {
        case "ArrowUp":
          keysRef.current.up = down;
          e.preventDefault();
          break;
        case "ArrowDown":
          keysRef.current.down = down;
          e.preventDefault();
          break;
        case "ArrowLeft":
          keysRef.current.left = down;
          e.preventDefault();
          break;
        case "ArrowRight":
          keysRef.current.right = down;
          e.preventDefault();
          break;
      }
    };
    const onKeyDown = (e: KeyboardEvent) => onKey(e, true);
    const onKeyUp = (e: KeyboardEvent) => onKey(e, false);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // DeviceOrientation
  useEffect(() => {
    const onOrientation = (e: DeviceOrientationEvent) => {
      if (e.beta != null) gyroRef.current.beta = e.beta;
      if (e.gamma != null) gyroRef.current.gamma = e.gamma;
    };
    window.addEventListener("deviceorientation", onOrientation);
    return () => window.removeEventListener("deviceorientation", onOrientation);
  }, []);

  const handleStartWithGyro = useCallback(() => {
    requestGyro();
    handleStart();
  }, [requestGyro, handleStart]);

  return (
    <div className="relative w-full h-screen bg-[#0f0f12] overflow-hidden">
      {/* Canvas container - full size */}
      <div
        ref={containerRef}
        tabIndex={0}
        className="absolute inset-0 w-full h-full outline-none"
        style={{ touchAction: "none" }}
        aria-label="Game area - use arrow keys to tilt"
      />

      {/* Start overlay */}
      {gameState === "idle" && (
        <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
          <div className="bg-black/80 backdrop-blur-md rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Balance the Ball</h1>
            <p className="text-gray-400 text-sm mb-4">
              Tilt the platform to keep the ball from falling. Collect gold coins for points and beat your best time!
            </p>
            <p className="text-gray-500 text-xs mb-6">
              Desktop: Arrow keys. Mobile: tilt device or use the buttons.
            </p>
            <button
              onClick={handleStartWithGyro}
              className={getButtonClasses("lg")}
            >
              Start Game
            </button>
          </div>
        </div>
      )}

      {/* HUD - top: back, timer, score, best */}
      {gameState === "playing" && (
        <>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
            <div className="bg-black/70 backdrop-blur-sm rounded-xl px-4 py-2 border border-cyan-500/30">
              <span className="text-cyan-300 font-bold font-mono text-lg tabular-nums">
                {survivalTime.toFixed(1)}s
              </span>
            </div>
            <div className="bg-black/70 backdrop-blur-sm rounded-xl px-4 py-2 border border-amber-500/30 flex items-center gap-1">
              <span className="text-amber-400 font-bold">★</span>
              <span className="text-amber-300 font-mono font-semibold">{score}</span>
            </div>
            <div className="bg-black/70 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
              <span className="text-gray-400 text-xs">Best </span>
              <span className="text-white font-mono font-semibold">{highScore.toFixed(1)}s</span>
            </div>
          </div>
          {milestoneToast && (
            <div className="absolute top-24 left-1/2 -translate-x-1/2 z-30 animate-bounce">
              <div className="bg-cyan-500/90 text-white font-bold px-6 py-3 rounded-2xl shadow-lg border-2 border-cyan-300">
                {milestoneToast}
              </div>
            </div>
          )}
          {dangerZone && (
            <div
              className="absolute inset-0 z-10 pointer-events-none border-[8px] border-red-500/50 rounded-lg animate-pulse"
              aria-hidden
            />
          )}
        </>
      )}

      {/* Back button - top left */}
      {gameState === "playing" && (
        <div className="absolute top-4 left-4 z-20">
          <button onClick={onBack} className={getButtonClasses("md")} aria-label="Back">
            Back
          </button>
        </div>
      )}

      {/* Touch controls - mobile, bottom */}
      {isMobile && (
        <div className="absolute bottom-6 left-0 right-0 z-20 flex flex-col items-center gap-2 pb-[env(safe-area-inset-bottom)]">
          <div className="flex gap-3">
            <span className="w-14 h-14 inline-block" aria-hidden />
            <button
              type="button"
              className="w-14 h-14 rounded-xl bg-white/20 border-2 border-white/30 text-white text-xl flex items-center justify-center active:bg-white/40 active:scale-95 select-none touch-manipulation"
              onTouchStart={() => (keysRef.current.up = true)}
              onTouchEnd={() => (keysRef.current.up = false)}
              onMouseDown={() => (keysRef.current.up = true)}
              onMouseUp={() => (keysRef.current.up = false)}
              onMouseLeave={() => (keysRef.current.up = false)}
              aria-label="Tilt up"
            >
              ▲
            </button>
            <span className="w-14 h-14 inline-block" aria-hidden />
          </div>
          <div className="flex gap-3 items-center">
            <button
              type="button"
              className="w-14 h-14 rounded-xl bg-white/20 border-2 border-white/30 text-white text-xl flex items-center justify-center active:bg-white/40 active:scale-95 select-none touch-manipulation"
              onTouchStart={() => (keysRef.current.left = true)}
              onTouchEnd={() => (keysRef.current.left = false)}
              onMouseDown={() => (keysRef.current.left = true)}
              onMouseUp={() => (keysRef.current.left = false)}
              onMouseLeave={() => (keysRef.current.left = false)}
              aria-label="Tilt left"
            >
              ◀
            </button>
            <span className="w-14 h-14 inline-block" aria-hidden />
            <button
              type="button"
              className="w-14 h-14 rounded-xl bg-white/20 border-2 border-white/30 text-white text-xl flex items-center justify-center active:bg-white/40 active:scale-95 select-none touch-manipulation"
              onTouchStart={() => (keysRef.current.right = true)}
              onTouchEnd={() => (keysRef.current.right = false)}
              onMouseDown={() => (keysRef.current.right = true)}
              onMouseUp={() => (keysRef.current.right = false)}
              onMouseLeave={() => (keysRef.current.right = false)}
              aria-label="Tilt right"
            >
              ▶
            </button>
          </div>
          <div className="flex gap-3">
            <span className="w-14 h-14 inline-block" aria-hidden />
            <button
              type="button"
              className="w-14 h-14 rounded-xl bg-white/20 border-2 border-white/30 text-white text-xl flex items-center justify-center active:bg-white/40 active:scale-95 select-none touch-manipulation"
              onTouchStart={() => (keysRef.current.down = true)}
              onTouchEnd={() => (keysRef.current.down = false)}
              onMouseDown={() => (keysRef.current.down = true)}
              onMouseUp={() => (keysRef.current.down = false)}
              onMouseLeave={() => (keysRef.current.down = false)}
              aria-label="Tilt down"
            >
              ▼
            </button>
            <span className="w-14 h-14 inline-block" aria-hidden />
          </div>
        </div>
      )}

      {/* Game Over overlay */}
      {gameState === "gameOver" && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-black/90 backdrop-blur-md rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Game Over</h2>
            <p className="text-gray-400 text-sm mb-4">The ball fell off the platform.</p>
            <div className="flex justify-center gap-2 mb-4">
              {[1, 2, 3].map((star) => (
                <span
                  key={star}
                  className={`text-2xl ${
                    finalTime >= (star === 1 ? 15 : star === 2 ? 45 : 90)
                      ? "text-amber-400"
                      : "text-gray-600"
                  }`}
                >
                  ★
                </span>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6 text-left">
              <div className="bg-white/5 rounded-xl p-3">
                <div className="text-gray-400 text-xs">Time</div>
                <div className="text-cyan-300 font-bold font-mono text-lg">{finalTime.toFixed(1)}s</div>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <div className="text-gray-400 text-xs">Coins</div>
                <div className="text-amber-300 font-bold text-lg">★ {score}</div>
              </div>
            </div>
            {isNewRecord && (
              <p className="text-amber-400 font-semibold text-sm mb-4">🏆 New best time!</p>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <button onClick={handleRestart} className={getButtonClasses("md")}>
                Play Again
              </button>
              <button onClick={onBack} className={getButtonClasses("md")}>
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
